/**
 * JSON merge patch (RFC 7386) helpers for the account config path (I-2510).
 * Pure functions, no Vue: apply a patch, compose two patches into one, and
 * compute the patch that turns one document into another.
 */

export type JsonObject = Record<string, unknown>;

export function isJsonObject(value: unknown): value is JsonObject {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** A deep copy of a JSON value (objects and arrays are copied, scalars kept). */
export function cloneJson<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => cloneJson(item)) as T;
  if (isJsonObject(value)) {
    const out: JsonObject = {};
    for (const [key, item] of Object.entries(value)) {
      if (item !== undefined) out[key] = cloneJson(item);
    }
    return out as T;
  }
  return value;
}

/** Deep equality of two JSON values; `undefined` object members count as absent. */
export function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length)
      return false;
    return a.every((item, index) => jsonEqual(item, b[index]));
  }
  if (!isJsonObject(a) || !isJsonObject(b)) return false;
  const aKeys = Object.keys(a).filter((key) => a[key] !== undefined);
  const bKeys = Object.keys(b).filter((key) => b[key] !== undefined);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => key in b && jsonEqual(a[key], b[key]));
}

/**
 * Applies a merge patch to a document (RFC 7386): objects merge member by
 * member, `null` removes a member, anything else (scalars, arrays) replaces.
 * Neither argument is modified.
 */
export function applyMergePatch(target: unknown, patch: unknown): unknown {
  if (!isJsonObject(patch)) return cloneJson(patch);
  const result: JsonObject = isJsonObject(target) ? { ...target } : {};
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (value === null) delete result[key];
    else result[key] = applyMergePatch(result[key], value);
  }
  return result;
}

/**
 * Deep merge of two documents without removal semantics: `over` wins member
 * by member, objects merge, scalars and arrays replace.
 */
export function mergeJson(base: unknown, over: unknown): unknown {
  if (over === undefined) return cloneJson(base);
  if (!isJsonObject(over) || !isJsonObject(base)) return cloneJson(over);
  const result: JsonObject = { ...base };
  for (const [key, value] of Object.entries(over)) {
    if (value === undefined) continue;
    result[key] = mergeJson(base[key], value);
  }
  return result;
}

/**
 * One merge patch with the effect of applying `first` and then `second`, or
 * `null` when no single merge patch has that effect: `first` sets a member to
 * null or a non-object and `second` sets it to an object — applied in turn the
 * object replaces whatever the document had, a single patch would merge into
 * it instead.
 */
export function composeMergePatches(
  first: JsonObject,
  second: JsonObject,
): JsonObject | null {
  const out: JsonObject = cloneJson(first);
  for (const [key, value] of Object.entries(second)) {
    if (value === undefined) continue;
    if (!isJsonObject(value)) {
      out[key] = cloneJson(value);
      continue;
    }
    if (!(key in first) || first[key] === undefined) {
      out[key] = cloneJson(value);
      continue;
    }
    const earlier = first[key];
    if (!isJsonObject(earlier)) return null;
    const nested = composeMergePatches(earlier, value);
    if (!nested) return null;
    out[key] = nested;
  }
  return out;
}

/** A copy of a document without null members (stored documents never hold null). */
export function stripNulls<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => stripNulls(item)) as T;
  if (!isJsonObject(value)) return value;
  const out: JsonObject = {};
  for (const [key, item] of Object.entries(value)) {
    if (item === null || item === undefined) continue;
    out[key] = stripNulls(item);
  }
  return out as T;
}

/**
 * The merge patch that turns `from` into `to`: members missing in `to` become
 * null, changed members carry their new value, nested objects are diffed
 * member by member. Empty when both are equal.
 */
export function diffMergePatch(from: JsonObject, to: JsonObject): JsonObject {
  const patch: JsonObject = {};
  for (const key of Object.keys(from)) {
    if (from[key] === undefined) continue;
    if (!(key in to) || to[key] === undefined || to[key] === null)
      patch[key] = null;
  }
  for (const [key, next] of Object.entries(to)) {
    if (next === undefined || next === null) continue;
    const previous = from[key];
    if (isJsonObject(previous) && isJsonObject(next)) {
      const nested = diffMergePatch(previous, next);
      if (Object.keys(nested).length) patch[key] = nested;
    } else if (!jsonEqual(previous, next)) {
      patch[key] = stripNulls(cloneJson(next));
    }
  }
  return patch;
}

/** True when a merge patch changes nothing. */
export function isEmptyMergePatch(patch: JsonObject): boolean {
  return Object.values(patch).every((value) => value === undefined);
}
